import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyModal = ({ isOpen, onClose }: PrivacyModalProps) => {
  // Prevent background scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-3xl border border-white/10 bg-[#0a0f1d] shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/5 bg-[#0a0f1d]">
              <div>
                <h2 className="text-2xl font-semibold text-white tracking-tight">CipherGenix Privacy Policy</h2>
                <p className="text-xs text-[#2dd4bf] mt-1 uppercase tracking-widest font-medium">Last Updated: 20 May 2025</p>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
              <div className="text-slate-300 space-y-8 leading-relaxed">
                
                <section className="p-6 rounded-2xl bg-white/5 border border-white/10 italic text-slate-200">
                  At CipherGenix, we are committed to protecting and respecting your
                  privacy. This Privacy Policy explains how we collect, use,
                  disclose, and safeguard your information when you visit our
                  website, use our products, or engage with us in other ways. Please
                  read this policy carefully. By using our services, you agree to
                  the practices described in this policy.
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-white mb-4">1. Information We Collect</h3>
                  <ul className="space-y-4">
                    <li><strong className="text-[#2dd4bf]">Personal Identifiable Information (PII):</strong> Name, email address, phone number, and any other information you voluntarily provide.</li>
                    <li><strong className="text-[#2dd4bf]">Usage Data:</strong> Information about how you interact with our website, including IP addresses, browser types, and pages viewed.</li>
                    <li><strong className="text-[#2dd4bf]">Cookies:</strong> Technologies used to enhance user experience, track usage, and gather analytics.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-white mb-4">2. How We Use Your Information</h3>
                  <ul className="list-disc list-inside space-y-2 marker:text-[#2dd4bf]">
                    <li>To provide, maintain, and improve our website and services.</li>
                    <li>To communicate with you and provide support.</li>
                    <li>To personalize your experience.</li>
                    <li>To comply with legal obligations and conduct business analysis.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-white mb-4">3. Sharing Your Information</h3>
                  <p className="mb-4">We may share information with <strong className="text-white">Service Providers</strong> (trusted third parties), during <strong className="text-white">Business Transfers</strong> (mergers/sales), or for <strong className="text-white">Legal Compliance</strong>.</p>
                </section>

                <section className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">4. Your Rights</h3>
                    <p className="text-sm">You have the right to access, correct, or delete your personal information, and opt-out of marketing communications.</p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">5. Data Retention</h3>
                    <p className="text-sm">We retain personal data only for as long as necessary to fulfill the purposes outlined or as required by law.</p>
                  </div>
                </section>

                <section className="border-t border-white/5 pt-8">
                  <h3 className="text-xl font-semibold text-white mb-4">6. International & Children's Privacy</h3>
                  <p className="text-sm mb-4">We do not knowingly collect data from children under 13. By using our services, you consent to data transfers to servers located in different countries, including the US.</p>
                </section>

                {/* Contact Section */}
                <div className="mt-12 p-8 rounded-3xl border border-[#2dd4bf]/20 bg-gradient-to-br from-[#2dd4bf]/5 to-transparent text-center">
                  <h2 className="text-2xl font-semibold text-white mb-4">Questions regarding privacy?</h2>
                  <p className="text-slate-400 mb-6 leading-relaxed">
                    If you have any questions or concerns about this Privacy Policy, <br />
                    please reach out to us at:
                  </p>
                  <div className="space-y-1">
                    <p className="text-white font-medium">CipherGenix Team</p>
                    <a href="mailto:ciphergenix@gmail.com" className="text-[#2dd4bf] hover:text-white text-xl font-medium transition-colors">
                      ciphergenix@gmail.com
                    </a>
                  </div>
                </div>

                <p className="text-center text-slate-600 text-xs tracking-widest uppercase pt-8">
                  End of Privacy Policy
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PrivacyModal;